/**
 * Cookie consent store (Pinia)
 *
 * Stores the visitor's cookie choice locally (so the banner does not reappear)
 * and best-effort logs it to the backend audit endpoint for compliance proof
 * (PIPEDA / Quebec Law 25 / GDPR).
 *
 * IMPORTANT: strictly-necessary cookies (auth/session/CSRF) are NOT gated by
 * this consent — they are exempt by law. Only functional/analytics/marketing
 * are opt-in and default to OFF.
 *
 * Native iOS: do not show the consent banner or settings sheet. The iOS app
 * does not load analytics/marketing cookies or advertising SDKs, so a
 * cookie/tracking prompt without App Tracking Transparency fails App Store
 * review (guideline 5.1.2(i)). Web and Android keep the existing prompt.
 */
import { defineStore } from 'pinia'
import apiClient from '@/api/client'
import { isIOS, isNative } from '@/native/platform'

function iosAppOmitsTrackingPrompts() {
  return isNative() && isIOS()
}

// Bump this when the cookie/privacy policy materially changes — visitors will
// be re-prompted for fresh consent.
export const POLICY_VERSION = '2026-08-13'

// Re-ask for consent after this many days (best practice ~12 months).
const CONSENT_TTL_DAYS = 365

const STORAGE_KEY = 'fsj_cookie_consent'
const CONSENT_ID_KEY = 'fsj_consent_id'

function _genId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID()
  } catch (_) { /* ignore */ }
  return 'c-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10)
}

function _loadChoice() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : null
  } catch (_) {
    return null
  }
}

function _isExpired(choice) {
  if (!choice || !choice.ts) return true
  const ageMs = Date.now() - choice.ts
  return ageMs > CONSENT_TTL_DAYS * 24 * 60 * 60 * 1000
}

export const useCookieConsentStore = defineStore('cookieConsent', {
  state: () => ({
    choice: _loadChoice(),
    showSettings: false,
  }),

  getters: {
    /**
     * Cookie / tracking UI (banner + settings). False on native iOS.
     * Web and Android still prompt.
     */
    promptsEnabled: () => !iosAppOmitsTrackingPrompts(),

    /** Banner must be shown: no choice yet, stale policy version, or expired. */
    needsBanner: (state) => {
      if (iosAppOmitsTrackingPrompts()) return false
      const c = state.choice
      if (!c) return true
      if (c.version !== POLICY_VERSION) return true
      if (_isExpired(c)) return true
      return false
    },

    /** Current category flags (necessary always true). */
    categories: (state) => ({
      necessary: true,
      functional: !!state.choice?.categories?.functional,
      analytics: !!state.choice?.categories?.analytics,
      marketing: !!state.choice?.categories?.marketing,
    }),

    allowAnalytics: (state) => !!state.choice?.categories?.analytics,
    allowMarketing: (state) => !!state.choice?.categories?.marketing,
    allowFunctional: (state) => !!state.choice?.categories?.functional,
  },

  actions: {
    ensureConsentId() {
      let id = null
      try {
        id = localStorage.getItem(CONSENT_ID_KEY)
        if (!id) {
          id = _genId()
          localStorage.setItem(CONSENT_ID_KEY, id)
        }
      } catch (_) {
        id = _genId()
      }
      return id
    },

    _persist(categories, action) {
      const choice = {
        categories: {
          necessary: true,
          functional: !!categories.functional,
          analytics: !!categories.analytics,
          marketing: !!categories.marketing,
        },
        action,
        version: POLICY_VERSION,
        ts: Date.now(),
      }
      this.choice = choice
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(choice))
      } catch (e) {
        console.warn('[CookieConsent] could not persist choice:', e?.message || e)
      }
      this._logToServer(choice).catch(() => {})
      console.log('[CookieConsent] saved:', action, choice.categories)
    },

    async _logToServer(choice) {
      try {
        await apiClient.post('/public/consent/', {
          consent_id: this.ensureConsentId(),
          action: choice.action,
          categories: choice.categories,
          policy_version: choice.version,
        })
      } catch (e) {
        // Logging is best-effort; the user's choice is already saved locally.
        console.warn('[CookieConsent] server log failed (non-blocking):', e?.message || e)
      }
    },

    acceptAll() {
      this._persist({ functional: true, analytics: true, marketing: true }, 'accept_all')
      this.showSettings = false
    },

    rejectAll() {
      this._persist({ functional: false, analytics: false, marketing: false }, 'reject_all')
      this.showSettings = false
    },

    saveCustom(categories) {
      this._persist(categories, 'custom')
      this.showSettings = false
    },

    openSettings() {
      if (iosAppOmitsTrackingPrompts()) return
      this.showSettings = true
    },

    closeSettings() {
      this.showSettings = false
    },
  },
})
